import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'

export interface DeleteBusinessProfileDialogProps {
  /**
   * Display name of the business profile to delete.
   */
  displayName: string
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Callback when the dialog should close.
   */
  onClose?: () => unknown
  /**
   * Callback when the confirm delete button is pressed.
   */
  onConfirmDeletePress?: () => unknown
  /**
   * Whether the delete operation is pending.
   */
  isDeletePending?: boolean
  /**
   * Test ID attribute.
   */
  testId?: string
  /**
   * Test ID attribute for the confirm delete button.
   */
  confirmDeleteTestId?: string
  /**
   * Test ID attribute for the cancel button.
   */
  cancelTestId?: string
}

/**
 * A dialog for confirming the deletion of a business profile.
 */
export function DeleteBusinessProfileDialog({
  isOpen,
  onClose,
  displayName,
  isDeletePending,
  onConfirmDeletePress,
  testId,
  confirmDeleteTestId,
  cancelTestId,
}: DeleteBusinessProfileDialogProps) {
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isDeletePending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>
          Permanently delete business profile?
        </ModalDialogTitle>
        <ModalDialogDescription>
          <p>
            Are you sure you want to permanently delete{' '}
            <span className="text-strong">{displayName}</span>?
          </p>
          <p>
            Please note that by deleting, it will still remain on any prior job
            where it has been provided as a business profile, but it won't show
            up in the list of existing business profiles on your account.
          </p>
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Yes, delete permanently"
            variant="default"
            onPress={onConfirmDeletePress}
            isPending={isDeletePending}
            testId={confirmDeleteTestId}
          />
          <Button
            label="No, cancel"
            variant="outline"
            onPress={onClose}
            isDisabled={isDeletePending}
            testId={cancelTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}
